import { useEffect, useState } from 'react'
import { getSessionAuth } from '../utils/sessionAuth'
import { ensureSessionUserId, getSessionUserId } from '../utils/sessionUser'
import {
  listClients,
  createClient,
  addClientToConversation,
  removeClientFromConversation,
  addClientToCalendarEvent,
  removeClientFromCalendarEvent,
} from '../api/crm'

export type ContactOption = {
  id: string
  first_name?: string
  last_name?: string
  company?: string
  cell?: string
  email?: string
  summary?: string
  fullName?: string
}

type Props = {
  open: boolean
  conversationId?: string
  dateId?: string
  linkedClientIds: Set<string>
  onClose: () => void
  onAdded: (contact: ContactOption) => void
  onRemoved?: (clientId: string) => void
  sessionUserId?: string | null
  size?: 'default' | 'wide'
  defaultCompany?: string
}

function ConversationContactPicker({
  open,
  conversationId,
  dateId,
  linkedClientIds,
  onClose,
  onAdded,
  onRemoved,
  sessionUserId,
  size = 'default',
  defaultCompany = '',
}: Props) {
  const [options, setOptions] = useState<ContactOption[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [addingId, setAddingId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [newForm, setNewForm] = useState({ first_name: '', last_name: '', email: '', company: defaultCompany })
  const [showCreateHint, setShowCreateHint] = useState(false)
  const newFormHasValue =
    newForm.first_name.trim() !== '' || newForm.last_name.trim() !== '' || newForm.email.trim() !== ''
  const [userId, setUserId] = useState<string | null>(() => sessionUserId ?? getSessionUserId())
  const localSelectionOnly = !dateId && !conversationId

  useEffect(() => {
    if (sessionUserId && sessionUserId !== userId) {
      setUserId(sessionUserId)
    }
  }, [sessionUserId, userId])

  useEffect(() => {
    if (newFormHasValue && showCreateHint) {
      setShowCreateHint(false)
    }
  }, [newFormHasValue, showCreateHint])

  useEffect(() => {
    if (!open) return
    const load = async () => {
      try {
        setLoading(true)
        setError(null)
        const ensuredId = userId || (await ensureSessionUserId(getSessionAuth()))
        if (!ensuredId) throw new Error('Missing user id')
        if (!userId) setUserId(ensuredId)
        const data = await listClients(ensuredId)
        const mapped = Array.isArray(data)
          ? data.map((c) => ({
              ...c,
              fullName: [c.first_name, c.last_name].filter(Boolean).join(' ') || 'Unknown',
            }))
          : []
        setOptions(mapped)
      } catch (err) {
        setOptions([])
        setError(err instanceof Error ? err.message : 'Unable to load contacts.')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [open, userId])

  const handleAddExisting = async (clientId: string) => {
    try {
      setAddingId(clientId)
      setError(null)
      if (localSelectionOnly) {
        const added = options.find((c) => c.id === clientId)
        if (added) onAdded(added)
        return
      }
      const ensuredId = userId || (await ensureSessionUserId(getSessionAuth()))
      if (!ensuredId) throw new Error('Missing user id')
      if (dateId) {
        await addClientToCalendarEvent(dateId, ensuredId, clientId)
      } else {
        if (!conversationId) throw new Error('Missing conversation/date id')
        await addClientToConversation(conversationId, ensuredId, clientId)
      }
      const added = options.find((c) => c.id === clientId)
      if (added) onAdded(added)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to add contact.')
    } finally {
      setAddingId(null)
    }
  }

  const handleRemoveExisting = async (clientId: string) => {
    try {
      setAddingId(clientId)
      setError(null)
      if (localSelectionOnly) {
        onRemoved?.(clientId)
        return
      }
      const ensuredId = userId || (await ensureSessionUserId(getSessionAuth()))
      if (!ensuredId) throw new Error('Missing user id')
      if (dateId) {
        await removeClientFromCalendarEvent(dateId, ensuredId, clientId)
      } else {
        if (!conversationId) throw new Error('Missing conversation/date id')
        await removeClientFromConversation(conversationId, ensuredId, clientId)
      }
      onRemoved?.(clientId)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to remove contact.')
    } finally {
      setAddingId(null)
    }
  }

  const handleCreateAndAdd = async () => {
    if (!newFormHasValue) {
      setShowCreateHint(true)
      return
    }
    try {
      setCreating(true)
      setError(null)
      const ensuredId = userId || (await ensureSessionUserId(getSessionAuth()))
      if (!ensuredId) throw new Error('Missing user id')
      const result = await createClient({
        user_id: ensuredId,
        conversation_id: conversationId && conversationId.trim() ? conversationId : null,
        client: {
          first_name: newForm.first_name,
          last_name: newForm.last_name,
          email: newForm.email,
          cell: '',
          company: newForm.company || defaultCompany,
          notes: '',
        },
      })
      const contactId = result.id
      if (!contactId) throw new Error('Missing contact id')
      const addedContact: ContactOption = {
        id: contactId,
        first_name: newForm.first_name,
        last_name: newForm.last_name,
        email: newForm.email,
        company: newForm.company || defaultCompany,
        fullName: [newForm.first_name, newForm.last_name].filter(Boolean).join(' ') || 'Unknown',
      }
      if (!localSelectionOnly) {
        if (dateId) {
          await addClientToCalendarEvent(dateId, ensuredId, contactId)
        } else {
          if (!conversationId) throw new Error('Missing conversation/date id')
          await addClientToConversation(conversationId, ensuredId, contactId)
        }
      }
      onAdded(addedContact)
      setNewForm({ first_name: '', last_name: '', email: '', company: defaultCompany })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to create contact.')
    } finally {
      setCreating(false)
    }
  }

  if (!open) return null

  const containerSize =
    size === 'wide'
      ? { maxWidth: 760, width: '96%', minWidth: 460 }
      : { maxWidth: 520, width: '90%', minWidth: 360 }

  return (
    <div className="modal-backdrop" onClick={onClose} style={{ alignItems: 'flex-start' }}>
      <div
        className="card-panel"
        style={{
          ...containerSize,
          margin: '80px auto',
          position: 'relative',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
          <div>
            <p className="eyebrow" style={{ marginBottom: '4px' }}>Add contact</p>
            <h3 style={{ margin: 0 }}>Link an existing contact</h3>
          </div>
          <button type="button" className="ghost-button" onClick={onClose}>
            Close
          </button>
        </div>
        {error && (
          <div className="muted" style={{ color: '#b42318', marginTop: 6 }}>{error}</div>
        )}
        <div style={{ marginTop: 10, maxHeight: '240px', overflowY: 'auto', border: '1px solid #e3e7ee', borderRadius: 12, padding: '8px' }}>
          {loading ? (
            <div className="muted">Loading contacts…</div>
          ) : (
            options.map((c) => (
              <div
                key={c.id}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 4px', borderBottom: '1px solid #eef1f6' }}
              >
                <div>
                  <div style={{ fontWeight: 700 }}>{c.fullName || 'Contact'}</div>
                  <div className="muted" style={{ fontSize: 13 }}>
                    {[c.email, c.cell].filter(Boolean).join(' • ')}
                  </div>
                </div>
                <button
                  type="button"
                  className={linkedClientIds.has(c.id) ? 'danger-button small' : 'primary-outline small'}
                  onClick={() => {
                    if (linkedClientIds.has(c.id)) {
                      handleRemoveExisting(c.id)
                    } else {
                      handleAddExisting(c.id)
                    }
                  }}
                  disabled={addingId === c.id}
                >
                  {linkedClientIds.has(c.id) ? 'Remove' : addingId === c.id ? 'Adding…' : 'Add'}
                </button>
              </div>
            ))
          )}
          {!loading && options.length === 0 && (
            <div className="muted" style={{ padding: '6px 4px' }}>No contacts found.</div>
          )}
        </div>
        <div style={{ marginTop: 16 }}>
          <p className="eyebrow" style={{ marginBottom: 6 }}>Create new</p>
          <div className="form-grid">
            <label>
              <span>First name</span>
              <input
                value={newForm.first_name}
                onChange={(e) => setNewForm((prev) => ({ ...prev, first_name: e.target.value }))}
              />
            </label>
            <label>
              <span>Last name</span>
              <input
                value={newForm.last_name}
                onChange={(e) => setNewForm((prev) => ({ ...prev, last_name: e.target.value }))}
              />
            </label>
            <label className="full-row">
              <span>Email</span>
              <input
                type="email"
                value={newForm.email}
                onChange={(e) => setNewForm((prev) => ({ ...prev, email: e.target.value }))}
              />
            </label>
          </div>
          <div style={{ display: 'flex', gap: '10px', marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              className="primary-button"
              onClick={handleCreateAndAdd}
              disabled={creating}
            >
              {creating ? 'Creating…' : 'Add new contact'}
            </button>
            <button type="button" className="ghost-button" onClick={onClose}>
              Cancel
            </button>
            {showCreateHint && !newFormHasValue && (
              <span className="issue-text">Fill out at least one field.</span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ConversationContactPicker
